// =============================================================================
// Notification emails via Resend (branded like invites).
// Invoice ready, reimbursement submitted. No payment links.
// =============================================================================

import { supabase } from "@/integrations/supabase/client";

const getBaseUrl = () =>
  typeof window !== "undefined"
    ? window.location.origin
    : (import.meta.env?.VITE_APP_URL ?? "https://portal.stravo.ai");

export interface SendInvoiceReadyEmailParams {
  recipientEmail: string;
  recipientName?: string | null;
  propertyName: string;
  periodStart: string;
  periodEnd: string;
  totalDueCents: number;
  cleaningCount: number;
  reimbursementsCount: number;
  viewInvoiceUrl: string;
}

export async function sendInvoiceReadyEmail(
  params: SendInvoiceReadyEmailParams
): Promise<{ ok: boolean; error?: string }> {
  const body = {
    recipientEmail: params.recipientEmail.trim().toLowerCase(),
    recipientName: params.recipientName ?? null,
    propertyName: params.propertyName || "your property",
    periodStart: params.periodStart,
    periodEnd: params.periodEnd,
    totalDueCents: params.totalDueCents,
    cleaningCount: params.cleaningCount,
    reimbursementsCount: params.reimbursementsCount,
    viewInvoiceUrl: params.viewInvoiceUrl,
  };
  const { data, error } = await supabase.functions.invoke("send-invoice-ready", { body });
  if (error) {
    console.error("[sendInvoiceReadyEmail] invoke error", { error: error.message, to: params.recipientEmail });
    return { ok: false, error: error.message };
  }
  const result = data as { ok?: boolean; error?: string } | null;
  if (result?.ok) return { ok: true };
  return { ok: false, error: result?.error ?? "Failed to send email" };
}

/** Option A: server resolves owner and reimbursement details. Use after cleaner submits. */
export interface SendReimbursementSubmittedEmailParams {
  propertyId: string;
  reimbursementId: string;
}

/** Option B: client provides full payload (e.g. when owner/team has recipient list). */
export interface SendReimbursementSubmittedEmailPayloadParams {
  recipientEmails: string[];
  propertyName: string;
  submittedByNameOrEmail: string;
  amountCents: number;
  description?: string | null;
  reimbursementsUrl: string;
}

export async function sendReimbursementSubmittedEmail(
  params: SendReimbursementSubmittedEmailParams | SendReimbursementSubmittedEmailPayloadParams
): Promise<{ ok: boolean; error?: string }> {
  const isServerLookup = "propertyId" in params && "reimbursementId" in params;
  const body = isServerLookup
    ? { propertyId: params.propertyId, reimbursementId: params.reimbursementId }
    : {
        recipientEmails: params.recipientEmails,
        propertyName: params.propertyName,
        submittedByNameOrEmail: params.submittedByNameOrEmail,
        amountCents: params.amountCents,
        description: params.description ?? null,
        reimbursementsUrl: params.reimbursementsUrl,
      };
  const { data, error } = await supabase.functions.invoke("send-reimbursement-submitted", { body });
  if (error) {
    console.error("[sendReimbursementSubmittedEmail] invoke error", { error: error.message });
    return { ok: false, error: error.message };
  }
  const result = data as { ok?: boolean; error?: string } | null;
  if (result?.ok) return { ok: true };
  return { ok: false, error: result?.error ?? "Failed to send email" };
}

/** Get owner and payee emails for an invoice (so client can send invoice notification to both). */
export async function getInvoiceRecipientEmails(invoiceId: string): Promise<{
  owner_email: string | null;
  payee_email: string | null;
} | null> {
  const { data, error } = await supabase.functions.invoke("get-invoice-recipient-emails", {
    body: { invoice_id: invoiceId },
  });
  if (error) {
    console.error("[getInvoiceRecipientEmails] error", { error: error.message, invoiceId });
    return null;
  }
  const result = data as { owner_email?: string | null; payee_email?: string | null } | null;
  if (!result) return null;
  return {
    owner_email: result.owner_email ?? null,
    payee_email: result.payee_email ?? null,
  };
}

export { getBaseUrl };

export async function sendInventoryLowStockEmail(params: {
  propertyId: string;
  cleaningTaskId?: string | null;
  lines: { item_name: string; detail: string }[];
  scopeLabel?: string;
}): Promise<{ ok: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke("send-inventory-low-stock", {
    body: {
      propertyId: params.propertyId,
      cleaningTaskId: params.cleaningTaskId ?? null,
      lines: params.lines,
      scopeLabel: params.scopeLabel ?? null,
    },
  });
  if (error) {
    console.error("[sendInventoryLowStockEmail]", error.message);
    return { ok: false, error: error.message };
  }
  const result = data as { ok?: boolean; error?: string } | null;
  if (result?.ok) return { ok: true };
  return { ok: false, error: result?.error ?? "Failed to send email" };
}

export async function sendMaintenanceIssuesEmail(params: {
  propertyId: string;
  taskId?: string | null;
  cleanerName?: string | null;
  issues: { room_name: string; description: string; severity?: string | null; photo_url?: string | null }[];
}): Promise<{ ok: boolean; error?: string }> {
  if (!params.issues.length) return { ok: true };
  const { data, error } = await supabase.functions.invoke("send-maintenance-issues", {
    body: {
      propertyId: params.propertyId,
      taskId: params.taskId ?? null,
      cleanerName: params.cleanerName ?? null,
      issues: params.issues,
    },
  });
  if (error) {
    console.error("[sendMaintenanceIssuesEmail]", error.message);
    return { ok: false, error: error.message };
  }
  const result = data as { ok?: boolean; error?: string } | null;
  if (result?.ok) return { ok: true };
  return { ok: false, error: result?.error ?? "Failed to send email" };
}

export async function sendCleaningCompleteEmail(params: {
  propertyId: string;
  taskId: string;
  cleanerName?: string | null;
  scheduledDate?: string | null;
  durationMinutes?: number | null;
}): Promise<{ ok: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke("send-cleaning-complete", {
    body: {
      propertyId: params.propertyId,
      taskId: params.taskId,
      cleanerName: params.cleanerName ?? null,
      scheduledDate: params.scheduledDate ?? null,
      durationMinutes: params.durationMinutes ?? null,
    },
  });
  if (error) {
    console.error("[sendCleaningCompleteEmail]", error.message);
    return { ok: false, error: error.message };
  }
  const result = data as { ok?: boolean; error?: string } | null;
  if (result?.ok) return { ok: true };
  return { ok: false, error: result?.error ?? "Failed to send email" };
}

export async function sendCleanerIncidentEmail(params: {
  propertyId: string;
  title: string;
  message: string;
  photoUrl?: string | null;
}): Promise<{ ok: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke("send-cleaner-incident", {
    body: {
      propertyId: params.propertyId,
      title: params.title,
      message: params.message,
      photoUrl: params.photoUrl ?? null,
    },
  });
  if (error) {
    console.error("[sendCleanerIncidentEmail]", error.message);
    return { ok: false, error: error.message };
  }
  const result = data as { ok?: boolean; error?: string } | null;
  if (result?.ok) return { ok: true };
  return { ok: false, error: result?.error ?? "Failed to send email" };
}
